/**
 * Duell: ein geteilter Link, zwei Rollen.
 *
 * Derselbe Bildschirm bedient beide Seiten, zeigt aber Verschiedenes:
 *
 *  - Wer den Link bekommen hat, sieht eine Herausforderung und einen Knopf zum
 *    Antreten.
 *  - Wer das Duell selbst gestartet hat, sieht den Spielstand. Fuer ihn ist
 *    dieser Bildschirm die Antwort auf die Frage, ob schon jemand gescrollt hat -
 *    frueher gab es darauf ueberhaupt keine.
 *
 * Unterschieden wird ueber die lokale Merkliste, nicht ueber den Server: Es gibt
 * keine Konten, an denen sich ein Besitzer festmachen liesse.
 *
 * Das Duell ist absichtlich kein Echtzeit-Spiel. Es gibt keine Lobby und keine
 * Wartezeit - ein Link genuegt, und der Gegner laeuft als Marke auf der eigenen
 * Bahn mit. Das funktioniert auch dann, wenn beide nie gleichzeitig online sind.
 */

import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { challengeUrl, fetchChallenge, type Challenge, type ChallengeAttempt } from '../api'
import { Header } from '../components/Header'
import { DISCIPLINE_LABELS, variantLabel, type SprintDistanceId } from '../engine/disciplines'
import { isOwnDuel, markDuelSeen } from '../engine/duels'
import { formatDistanceInline, formatTime } from '../engine/units'
import { LangstreckeScreen } from './Langstrecke'
import { SprintScreen } from './Sprint'
import { SprungScreen } from './Sprung'

export function DuellScreen() {
  const { code } = useParams<{ code: string }>()
  const [challenge, setChallenge] = useState<Challenge | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [running, setRunning] = useState(false)
  const [shareNote, setShareNote] = useState<string | null>(null)
  const [own] = useState(() => (code ? isOwnDuel(code) : false))

  const load = useCallback(async () => {
    if (!code) return
    const response = await fetchChallenge(code)
    if (!response.ok) {
      setError(response.error)
      return
    }
    setChallenge(response.data)
    // Der aktuelle Stand gilt jetzt als gesehen - das Abzeichen auf der
    // Startseite erlischt.
    if (isOwnDuel(code)) markDuelSeen(code, response.data.attempts.length)
  }, [code])

  useEffect(() => {
    void load()
  }, [load])

  const handleAnswered = useCallback(() => {
    setRunning(false)
    void load()
  }, [load])

  if (error) {
    return (
      <Frame>
        <p className="message message--error">{error}</p>
        <p className="page__note">Der Link ist womöglich abgelaufen oder wurde nie vergeben.</p>
      </Frame>
    )
  }

  if (!challenge) {
    return (
      <Frame>
        <p className="page__note">Wird geladen…</p>
      </Frame>
    )
  }

  const isSprint = challenge.discipline === 'sprint'
  const format = (attempt: ChallengeAttempt): string =>
    isSprint ? `${formatTime(attempt.timeMs)} s` : formatDistanceInline(attempt.distanceCm)
  const variant = variantLabel(challenge.variant)

  if (running) {
    const shared = {
      challengeCode: challenge.code,
      rivalName: challenge.challenger.playerName,
      onAnswered: handleAnswered,
    }
    if (challenge.discipline === 'sprung') {
      return <SprungScreen {...shared} rivalCm={challenge.challenger.distanceCm} />
    }
    if (challenge.discipline === 'langstrecke') {
      return <LangstreckeScreen {...shared} rivalCm={challenge.challenger.distanceCm} />
    }
    return (
      <SprintScreen
        {...shared}
        forcedDistanceId={(challenge.variant ?? '100m') as SprintDistanceId}
        rivalTimeMs={challenge.challenger.timeMs}
      />
    )
  }

  // Fuehre ich noch? Beim Sprint gewinnt die kuerzere Zeit, sonst die groessere Weite.
  const best = challenge.attempts[0]
  const leading =
    !best ||
    (isSprint
      ? challenge.challenger.timeMs <= best.timeMs
      : challenge.challenger.distanceCm >= best.distanceCm)

  return (
    <div className="screen screen--page" data-discipline={challenge.discipline}>
      <Header title={own ? 'Dein Duell' : 'Duell'} />
      <div className="page">
        <div className="duel">
          <p className="duel__intro">
            {own
              ? 'Deine Vorgabe'
              : `${challenge.challenger.playerName} fordert dich heraus`}
          </p>
          <p className="duel__value">{format(challenge.challenger)}</p>
          <p className="duel__discipline">
            {DISCIPLINE_LABELS[challenge.discipline]}
            {variant ? ` · ${variant}` : ''}
          </p>
        </div>

        {own ? (
          <>
            {challenge.attempts.length === 0 ? (
              <div className="empty">
                <p className="empty__title">Noch keine Antwort</p>
                <p className="empty__text">
                  Schick den Link nochmal herum – ohne Gegner kein Duell.
                </p>
              </div>
            ) : (
              <p className={leading ? 'verdict verdict--ahead' : 'verdict'}>
                {leading ? 'Du führst noch.' : 'Du wurdest überholt.'}
              </p>
            )}

            <button
              className="button button--wide"
              type="button"
              onClick={() => void shareAgain(challenge, format(challenge.challenger), setShareNote)}
            >
              Link erneut teilen
            </button>
            {shareNote && <p className="message message--ok">{shareNote}</p>}
          </>
        ) : (
          <button
            className="button button--primary button--wide"
            type="button"
            onClick={() => setRunning(true)}
          >
            Antreten
          </button>
        )}

        {challenge.attempts.length > 0 && (
          <>
            <h2 className="section">
              {challenge.attempts.length === 1 ? 'Eine Antwort' : `${challenge.attempts.length} Antworten`}
            </h2>
            <ol className="ranking">
              {challenge.attempts.map((attempt, index) => (
                <li key={`${attempt.playerName}-${attempt.createdAt}`} className="ranking__row">
                  <span className="ranking__rank">{index + 1}</span>
                  <span className="ranking__name">{attempt.playerName}</span>
                  <span className="ranking__value">{format(attempt)}</span>
                </li>
              ))}
            </ol>
          </>
        )}
      </div>
    </div>
  )
}

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="screen screen--page">
      <Header title="Duell" />
      <div className="page">{children}</div>
    </div>
  )
}

async function shareAgain(
  challenge: Challenge,
  ownValue: string,
  notify: (message: string) => void,
): Promise<void> {
  const url = challengeUrl(challenge.code)
  const text = `Ich bin ${ownValue} gescrollt. Schlag das.`

  if (navigator.share) {
    try {
      await navigator.share({ title: 'Scroll-o-Meter', text, url })
      return
    } catch {
      // Teilen-Dialog abgebrochen - dann bleibt der Weg ueber die Zwischenablage.
    }
  }

  try {
    await navigator.clipboard.writeText(url)
    notify('Link kopiert.')
  } catch {
    notify(url)
  }
}
